import React, { useState, useEffect, useCallback, useRef } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import AdminModal from '../../components/admin/AdminModal';
import Badge from '../../components/admin/Badge';
import EmptyState from '../../components/admin/EmptyState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

function AdminDeployments({ subscriptions, apiFetch, refreshSubscriptions, toast }) {
  const [servers, setServers] = useState([]);
  const [deployModal, setDeployModal] = useState(null);
  const [selectedServer, setSelectedServer] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [logOutput, setLogOutput] = useState('');
  const [logStatus, setLogStatus] = useState(null);
  const [logModal, setLogModal] = useState(null);
  const [actionLogs, setActionLogs] = useState([]);
  const pollRef = useRef(null);
  const logEndRef = useRef(null);
  const [deploymentMode, setDeploymentMode] = useState('manual');
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState('');
  const [savingMode, setSavingMode] = useState(false);
  const [undeployConfirm, setUndeployConfirm] = useState(null);
  const [suspendConfirm, setSuspendConfirm] = useState(null);

  const pending = subscriptions.filter(s => s.service_status === 'pending' && s.status !== 'canceled');
  const deployed = subscriptions.filter(s => ['active', 'suspended'].includes(s.service_status));

  const loadServers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/servers');
      const data = await res.json();
      setServers(data.servers || []);
    } catch {}
  }, [apiFetch]);

  const loadDeploymentMode = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/settings/deployment-mode');
      const data = await res.json();
      setDeploymentMode(data.mode || 'manual');
      setN8nWebhookUrl(data.n8n_webhook_url || '');
    } catch {}
  }, [apiFetch]);

  useEffect(() => { loadServers(); loadDeploymentMode(); }, [loadServers, loadDeploymentMode]);
  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);
  useEffect(() => { if (logEndRef.current) logEndRef.current.scrollTop = logEndRef.current.scrollHeight; }, [logOutput]);

  const saveDeploymentMode = async () => {
    setSavingMode(true);
    try {
      const res = await apiFetch('/api/admin/settings/deployment-mode', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: deploymentMode, n8n_webhook_url: n8nWebhookUrl })
      });
      if (res.ok) toast.success('Saved', 'Deployment mode updated');
      else { const d = await res.json(); toast.error('Error', d.error || 'Unknown'); }
    } catch (err) { toast.error('Error', err.message); }
    finally { setSavingMode(false); }
  };

  const pollLog = useCallback((logId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/admin/deployment-log/${logId}`);
        const data = await res.json();
        setLogOutput(data.log.output || '');
        if (['success', 'failed', 'sent_to_n8n'].includes(data.log.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLogStatus(data.log.status);
          setDeploying(false);
          refreshSubscriptions();
        }
      } catch {}
    }, 2000);
  }, [apiFetch, refreshSubscriptions]);

  const handleDeploy = async () => {
    if (deploymentMode === 'ssh' && !selectedServer) return;
    if (!deployModal) return;
    setDeploying(true); setLogOutput(''); setLogStatus(null);
    try {
      const body = { subscriptionId: deployModal.id };
      if (selectedServer) body.serverId = parseInt(selectedServer);
      const res = await apiFetch('/api/admin/deploy', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (data.logId) pollLog(data.logId);
      else if (data.success) {
        setLogOutput(data.message || 'Done');
        setLogStatus('success');
        setDeploying(false);
        refreshSubscriptions();
        toast.success('Deployed', data.message || 'Deployment successful');
      } else {
        setLogOutput('Error: ' + (data.error || 'Unknown'));
        setDeploying(false);
      }
    } catch (err) { setLogOutput('Error: ' + err.message); setDeploying(false); }
  };

  const handleAction = async (action, subId) => {
    setDeploying(true); setLogOutput(''); setLogStatus(null);
    setDeployModal({ id: subId, _actionOnly: true });
    try {
      const res = await apiFetch(`/api/admin/${action}/${subId}`, { method: 'POST' });
      const data = await res.json();
      if (data.logId) pollLog(data.logId);
      else if (data.success) {
        setLogOutput(data.message || 'Done');
        setLogStatus('success');
        setDeploying(false);
        refreshSubscriptions();
        toast.success('Success', data.message || `${action} completed`);
      } else {
        setLogOutput('Error: ' + (data.error || 'Unknown'));
        setDeploying(false);
      }
    } catch (err) { setLogOutput('Error: ' + err.message); setDeploying(false); }
  };

  const viewLogs = async (subId) => {
    try {
      const res = await apiFetch(`/api/admin/deployment-logs/${subId}`);
      const data = await res.json();
      setActionLogs(data.logs || []);
      setLogModal(subId);
    } catch {}
  };

  const deployBtnLabel = deploymentMode === 'manual' ? 'Activate' : deploymentMode === 'n8n' ? 'Send to n8n' : 'Deploy';
  const startBtnLabel = deploymentMode === 'manual' ? 'Activate Now' : deploymentMode === 'n8n' ? 'Send to n8n' : 'Start Deployment';

  const pendingColumns = [
    { key: 'email', header: 'Email', render: row => row.customerEmail },
    { key: 'plan', header: 'Plan', render: row => <span className="admin-mono">{row.planSlug}</span> },
    { key: 'domain', header: 'Domain', render: row => row.domain || '—' },
    { key: 'created', header: 'Created', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => new Date(row.created * 1000).toLocaleDateString() },
    { key: 'action', header: 'Action', render: row => (
      <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => { setDeployModal(row); setLogOutput(''); setLogStatus(null); setSelectedServer(''); }}>
        <i className="fa-solid fa-rocket" /> {deployBtnLabel}
      </button>
    )},
  ];

  const deployedColumns = [
    { key: 'email', header: 'Email', render: row => row.customerEmail },
    { key: 'plan', header: 'Plan', render: row => <span className="admin-mono">{row.planSlug}</span> },
    { key: 'domain', header: 'Domain', render: row => row.domain || '—' },
    { key: 'srv', header: 'Server', render: row => row.srv || '—' },
    { key: 'status', header: 'Status', render: row => <Badge variant={row.service_status}>{row.service_status}</Badge> },
    { key: 'actions', header: 'Actions', render: row => (
      <div className="admin-btn-group">
        {row.service_status === 'active' && (
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => setSuspendConfirm(row.id)}>Suspend</button>
        )}
        {row.service_status === 'suspended' && (
          <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => handleAction('unsuspend', row.id)}>Unsuspend</button>
        )}
        <button className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => setUndeployConfirm(row.id)}>Undeploy</button>
        <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => viewLogs(row.id)}>Logs</button>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Deployments" description="Deploy, suspend, and manage service instances" />

      {/* Deployment Mode Settings */}
      <div className="admin-card" style={{ marginBottom: '24px' }}>
        <div className="admin-card-header">
          <h3 className="admin-card-title">Deployment Mode</h3>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {['manual', 'ssh', 'n8n'].map(mode => (
              <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="radio" name="deploymentMode" value={mode} checked={deploymentMode === mode} onChange={() => setDeploymentMode(mode)} />
                <span style={{ fontWeight: deploymentMode === mode ? '600' : '400' }}>
                  {mode === 'manual' ? 'Manual' : mode === 'ssh' ? 'SSH' : 'n8n Webhook'}
                </span>
              </label>
            ))}
          </div>
          {deploymentMode === 'n8n' && (
            <div style={{ flex: 1, minWidth: '300px' }}>
              <label className="admin-label" style={{ marginBottom: '4px' }}>n8n Webhook URL</label>
              <input className="admin-input" type="url" value={n8nWebhookUrl} onChange={e => setN8nWebhookUrl(e.target.value)} placeholder="https://your-n8n.example.com/webhook/..." style={{ width: '100%' }} />
            </div>
          )}
          <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={saveDeploymentMode} disabled={savingMode}>
            {savingMode ? 'Saving...' : 'Save'}
          </button>
        </div>
        <p className="admin-text-secondary" style={{ margin: '8px 0 0', fontSize: '12px' }}>
          {deploymentMode === 'manual' && 'Manual: Only updates Stripe metadata and sends emails. No infrastructure actions.'}
          {deploymentMode === 'ssh' && 'SSH: Connects to servers via SSH to manage Docker containers.'}
          {deploymentMode === 'n8n' && 'n8n: Sends deployment actions to an n8n webhook URL for external automation.'}
        </p>
      </div>

      {/* Pending Queue */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Pending Deployments ({pending.length})</h3>
        </div>
        {pending.length === 0 ? (
          <EmptyState icon="fa-solid fa-inbox" title="No pending deployments" description="All subscriptions have been deployed." />
        ) : (
          <DataTable columns={pendingColumns} data={pending} />
        )}
      </div>

      {/* Active Deployments */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Active Deployments ({deployed.length})</h3>
        </div>
        {deployed.length === 0 ? (
          <EmptyState icon="fa-solid fa-server" title="No active deployments" />
        ) : (
          <DataTable columns={deployedColumns} data={deployed} />
        )}
      </div>

      {/* Deploy Modal */}
      <AdminModal
        open={!!deployModal}
        onClose={() => { if (!deploying) { setDeployModal(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } } }}
        title={deployModal?._actionOnly ? 'Action in Progress' : `${deployBtnLabel}: ${deployModal?.customerEmail || ''}`}
        width="620px"
      >
        {deployModal && !deployModal._actionOnly && !logStatus && (
          <>
            <p className="admin-text-secondary" style={{ margin: '0 0 8px' }}>
              Plan: <strong>{deployModal.planSlug}</strong> | Domain: <strong>{deployModal.domain || 'N/A'}</strong>
            </p>
            {deploymentMode === 'ssh' && (
              <div className="admin-form-group">
                <label className="admin-form-label">Select Server</label>
                <select className="admin-select" value={selectedServer} onChange={e => setSelectedServer(e.target.value)}>
                  <option value="">— Choose a server —</option>
                  {servers.filter(s => s.status === 'active').map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.hostname}) — {s.current_instances}/{s.max_instances}</option>
                  ))}
                </select>
              </div>
            )}
            {deploymentMode !== 'ssh' && (
              <p className="admin-text-secondary" style={{ margin: '0 0 12px', fontSize: '13px', fontStyle: 'italic' }}>
                {deploymentMode === 'manual' ? 'Metadata will be updated and activation email sent. No infrastructure action.' : 'Action will be sent to n8n webhook for processing.'}
              </p>
            )}
            <button className="admin-btn admin-btn--primary" onClick={handleDeploy} disabled={deploying || (deploymentMode === 'ssh' && !selectedServer)}>
              {deploying ? 'Processing...' : startBtnLabel}
            </button>
          </>
        )}
        {logOutput && (
          <div ref={logEndRef} className="admin-log-viewer" style={{ marginTop: 16 }}>{logOutput}</div>
        )}
        {logStatus && (
          <div className={`admin-alert ${logStatus === 'success' || logStatus === 'sent_to_n8n' ? 'admin-alert--success' : 'admin-alert--danger'}`} style={{ marginTop: 12 }}>
            {logStatus === 'success' ? 'Completed successfully!' : logStatus === 'sent_to_n8n' ? 'Sent to n8n webhook successfully!' : 'Action failed. Check logs above.'}
          </div>
        )}
        {(logStatus || !deploying) && deployModal && (
          <button className="admin-btn admin-btn--secondary" style={{ marginTop: 12 }} onClick={() => { setDeployModal(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}>Close</button>
        )}
      </AdminModal>

      {/* Logs Modal */}
      <AdminModal
        open={!!logModal}
        onClose={() => setLogModal(null)}
        title="Deployment Logs"
        width="620px"
        footer={<button className="admin-btn admin-btn--secondary" onClick={() => setLogModal(null)}>Close</button>}
      >
        {actionLogs.length === 0 ? (
          <EmptyState icon="fa-solid fa-file-lines" title="No deployment logs" />
        ) : (
          actionLogs.map(log => (
            <div key={log.id} className="admin-card" style={{ marginBottom: 12 }}>
              <div className="admin-flex-between">
                <span><strong>{log.action}</strong> — {log.domain}</span>
                <Badge variant={log.status === 'success' || log.status === 'sent_to_n8n' ? 'active' : log.status === 'failed' ? 'suspended' : 'pending'}>{log.status}</Badge>
              </div>
              <div className="admin-text-secondary" style={{ fontSize: 12, margin: '4px 0' }}>
                {new Date(log.started_at).toLocaleString()}
                {log.error_message && <span className="admin-text-danger"> — {log.error_message}</span>}
              </div>
              {log.output && <div className="admin-log-viewer" style={{ maxHeight: 200, marginTop: 8 }}>{log.output}</div>}
            </div>
          ))
        )}
      </AdminModal>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={!!undeployConfirm}
        onClose={() => setUndeployConfirm(null)}
        onConfirm={() => { handleAction('undeploy', undeployConfirm); setUndeployConfirm(null); }}
        title="Undeploy Service"
        message="Undeploy this service? Containers will be stopped and removed."
        confirmText="Undeploy"
        confirmVariant="danger"
      />
      <ConfirmDialog
        open={!!suspendConfirm}
        onClose={() => setSuspendConfirm(null)}
        onConfirm={() => { handleAction('suspend', suspendConfirm); setSuspendConfirm(null); }}
        title="Suspend Service"
        message="Suspend this service? The containers will be stopped."
        confirmText="Suspend"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminDeployments;
